import {
  createInertiaApp,
  router,
  type ResolvedComponent,
} from '@inertiajs/react'
import { type ReactNode, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import Layout from '../components/Layout'

// Google Analytics: o gtag só existe quando o layout injetou o script
// (GA_MEASUREMENT_ID no env). O Inertia troca de página sem recarregar, então
// o page_view automático do GA veria uma visita só — este listener envia um
// page_view por navegação, incluindo a primeira (o `navigate` também dispara
// no load inicial; por isso o config no layout usa send_page_view: false).
router.on('navigate', () => {
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag !== 'function') return

  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title,
  })
})

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - BeWave` : 'BeWave'),

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    // O `!` exclui arquivo de teste do bundle. Sem ele, um `*.test.tsx` colocado
    // em pages/ é importado com eager e AVALIADO no browser: o `vi.mock()` do topo
    // estoura antes do createRoot().render() e toda página fica em branco — sem o
    // build falhar, porque as devDependencies estão instaladas na imagem.
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      ['../pages/**/*.tsx', '!../pages/**/*.test.tsx'],
      { eager: true },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Use default layout for all pages
    page.default.layout ||= (page: ReactNode) => <Layout>{page}</Layout>

    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
